import { Container, Ticker, LoaderResource, Texture } from "pixi.js";
import { AssetLoader } from "../loader/AssetLoader";
import { StorageManager } from "../storage/StorageManager";

/**
 *
 * @export
 * @class SpineAnimation
 * @extends {Container}
 */
export class SpineAnimation extends Container {
  /**
   */
  public static ticker = new Ticker();

  /**
   */
  public static resources: LoaderResource[] = [];

  get timeScale() {
    return this.spine.state.timeScale;
  }
  set timeScale(value) {
    this.spine.state.timeScale = value;
  }

  /**
   *
   * @param name
   */
  public static getResource(name: string): PIXI.spine.core.SkeletonData | undefined {
    let skeletonJson;
    const resource = AssetLoader.spineResources.find(el => el.name === name);
    if (resource) {
      skeletonJson = resource.spineData;
      skeletonJson.spineScale = (resource.metadata as any).spineScale || 1;
    }
    return skeletonJson;
  }

  public storage: StorageManager;

  /**
   * Spine animation instance
   */
  public spine: PIXI.spine.Spine;

  /**
   */
  public get tracks(): PIXI.spine.core.TrackEntry[] | undefined {
    return this.spine.state && this.spine.state.tracks;
  }

  /**
   */
  public get autoUpdate(): boolean {
    return this.spine.autoUpdate;
  }
  public set autoUpdate(value: boolean) {
    this.spine.autoUpdate = value;
  }

  /**
   */
  constructor(skeletonData: PIXI.spine.core.SkeletonData | string) {
    super();
    if (typeof skeletonData === "string") skeletonData = SpineAnimation.getResource(skeletonData);
    if (!skeletonData) console.warn(`Couldn't find Spine resource ${skeletonData}`);
    else {
      SpineAnimation.ticker.add(this._update, this);
      this.spine = new PIXI.spine.Spine(skeletonData);
      this.spine.scale.set((skeletonData as any).spineScale);
      this.addChild(this.spine);
      this.autoUpdate = false;
    }
  }

  /**
   * @param options
   */
  public destroy(options?: PIXI.DestroyOptions) {
    SpineAnimation.ticker.remove(this._update, this);
    super.destroy(options);
  }

  /**
   *
   * @param dt
   */
  public update(dt?: number) {}

  /**
   */
  private _update() {
    if (!this.spine.autoUpdate && this.worldVisible)
      this.spine.update(SpineAnimation.ticker.elapsedMS / 1000 || 0);
    this.update(SpineAnimation.ticker.deltaTime);
  }

  /**
   */
  public setToSetupPose() {
    this.spine.skeleton.setToSetupPose();
  }

  /**
   * Set skin.
   *
   * @param name
   */
  public setSkinByName(name: string) {
    (this.spine.skeleton.setSkin as Function)(null);
    this.spine.skeleton.setSkinByName(name);
  }

  /**
   * Set state mix time.
   *
   * @param from
   * @param to
   * @param time
   */
  public setMix(from: string, to: string, time: number) {
    this.spine.stateData.setMix(from, to, time);
  }

  /**
   *
   * @param name
   */
  public getAnimationDuration(name: string): number | undefined {
    const animation = this.getAnimation(name);
    return animation && animation.duration;
  }

  /**
   *
   * @param name
   */
  public getAnimation(name: string): PIXI.spine.core.Animation | undefined {
    return this.spine.spineData.findAnimation(name);
  }

  /**
   * Set state mix time.
   */
  public setDefaultMix(time: number) {
    this.spine.stateData.defaultMix = time;
  }

  /**
   * Set animation.
   */
  public setAnimation(
    trackIndex: number,
    name: string,
    loop: boolean
  ): PIXI.spine.core.TrackEntry | undefined {
    const trackEntry = this.spine.state.setAnimation(trackIndex, name, loop);
    this.updateListener();
    this.spine.update(0);
    return trackEntry;
  }

  /**
   */
  public updateListener() {
    for (let i = 0; i < this.tracks.length; i++) {
      this.tracks[i].listener = {
        event: this.onAnimationEvent.bind(this)
      };
    }
  }

  /**
   */
  public onAnimationEvent(trackEntry, event) {
    if (event.data.audio) {
      if (this.app && this.app.sound) {
        this.app.sound.play(event.data.name);
      }
    }
  }

  /**
   * Add animation to track queue.
   */
  public addAnimation(
    trackIndex: number,
    name: string,
    loop: boolean,
    delay: number
  ): PIXI.spine.core.TrackEntry | undefined {
    const trackEntry = this.spine.state.addAnimation(trackIndex, name, loop, delay);
    this.updateListener();
    return trackEntry;
  }

  /**
   * Get sprites with slot name.
   */
  public getBone(boneName: string): PIXI.spine.core.BoneData | undefined {
    let bone;
    const bones = this.spine.spineData.bones;
    if (bones) for (const b of bones) if (b.name === boneName) bone = b;
    return bone;
  }

  /**
   * Get sprites with slot name.
   */
  public getSlotSprites(slotName: string): Container | undefined {
    let slot;
    var slots = this.spine.skeleton.findSlot(slotName);
    if (slots) {
      for (var key in slots.sprites) {
        if (slots.sprites[key]) {
          slot = slots.sprites[key];
        }
      }
    }
    return slot;
  }

  /**
   * Set sprites with slot name's visibility.
   */
  public setSlotSpritesVisible(slotName: string, visible?: boolean) {
    var slots = this.spine.skeleton.findSlot(slotName);
    if (slots) {
      for (var key in slots.sprites) {
        if (slots.sprites[key]) {
          slots.sprites[key].visible = !!visible;
        }
      }
    }
  }

  /**
   * Get a slot texture by name.
   */
  public getSlotTexture(slotName: string): Texture | undefined {
    let texture;
    const index = this.spine.skeleton.findSlotIndex(slotName);
    if (index !== -1) {
      const slot = this.spine.skeleton.slots[index];
      const attachment: any = slot && slot.attachment;
      texture = attachment && attachment.region && attachment.region.texture;
    }
    return texture;
  }
}

SpineAnimation.ticker.start();
